// Screen-space retro effects drawn into the 2D scene before the CRT pass.

// Static / snow: a few pre-rendered noise tiles drawn at random offsets.
export class NoiseField {
  constructor(size = 128, variants = 6) {
    this.size = size;
    this.tiles = [];
    for (let v = 0; v < variants; v++) {
      const c = document.createElement('canvas');
      c.width = size;
      c.height = size;
      const ctx = c.getContext('2d');
      const img = ctx.createImageData(size, size);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const n = (Math.random() * 255) | 0;
        d[i] = n;
        d[i + 1] = n;
        d[i + 2] = n;
        d[i + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      this.tiles.push(c);
    }
  }

  draw(ctx, W, H, alpha = 0.05) {
    const tile = this.tiles[(Math.random() * this.tiles.length) | 0];
    const s = this.size;
    const ox = -((Math.random() * s) | 0);
    const oy = -((Math.random() * s) | 0);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = 'lighter';
    for (let y = oy; y < H; y += s) {
      for (let x = ox; x < W; x += s) ctx.drawImage(tile, x, y);
    }
    ctx.restore();
  }
}

// Cursor reveals a grid of pixels that fade out.
export class PixelTrail {
  constructor(size = 12, fade = 800) {
    this.size = size;
    this.fade = fade;
    this.cells = new Map();
  }

  add(x, y, now) {
    const s = this.size;
    const cx = Math.floor(x / s);
    const cy = Math.floor(y / s);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        this.cells.set(`${cx + dx},${cy + dy}`, now);
      }
    }
  }

  draw(ctx, now) {
    const s = this.size;
    for (const [key, t] of this.cells) {
      const age = now - t;
      if (age > this.fade) {
        this.cells.delete(key);
        continue;
      }
      const [cx, cy] = key.split(',').map(Number);
      const a = 0.22 * (1 - age / this.fade);
      ctx.fillStyle = `rgba(144,144,144,${a.toFixed(3)})`;
      ctx.fillRect(cx * s, cy * s, s, s);
    }
  }
}

// Random horizontal glitch blocks flashing across the screen.
export class GlitchBlocks {
  constructor() {
    this.list = [];
    this.lastSpawn = 0;
  }

  update(now, W, H, enabled = true) {
    if (enabled && now - this.lastSpawn > 40) {
      this.lastSpawn = now;
      if (Math.random() > 0.92) {
        this.list.push({
          x: Math.random() * W,
          y: Math.random() * H,
          w: 60 + Math.random() * 160,
          h: 6 + Math.random() * 20,
          skew: Math.random() * 20 - 10,
          alpha: 0.3 + Math.random() * 0.4,
          until: now + 300
        });
      }
    }
    this.list = this.list.filter(b => b.until > now);
  }

  draw(ctx) {
    if (!this.list.length) return;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (const b of this.list) {
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.transform(1, 0, Math.tan((b.skew * Math.PI) / 180), 1, 0, 0);
      ctx.fillStyle = `rgba(255,255,255,${(b.alpha * 0.25).toFixed(3)})`;
      ctx.fillRect(0, 0, b.w, b.h);
      ctx.restore();
    }
    ctx.restore();
  }
}

// Glitch state for a short string (the logo): character corruption, sliced
// horizontal tearing, RGB channel split, dropouts and vertical jitter.
export class TextGlitch {
  constructor(original) {
    this.original = original;
    this.text = original;
    this.until = 0;
    this.lastTick = 0;
    this.slices = [];
    this.dropouts = [];
    this.rgb = 0;
    this.jitterY = 0;
    this.big = false;
  }

  get(now, enabled = true) {
    if (now < this.until) {
      // re-roll slice offsets every ~40ms so the tear crawls
      if (now - this.lastTick > 40) {
        this.lastTick = now;
        for (const sl of this.slices) sl.dx = (Math.random() * 2 - 1) * sl.max;
        this.jitterY = this.big ? Math.random() * 6 - 3 : 0;
      }
      return { text: this.text, active: true, slices: this.slices, dropouts: this.dropouts, rgb: this.rgb, jitterY: this.jitterY };
    }
    this.text = this.original;
    if (enabled && now - this.lastTick > 100) {
      this.lastTick = now;
      const roll = Math.random();
      if (roll > 0.93) {
        // big glitch: corrupted characters, many slices, strong rgb split
        this.big = roll > 0.975;
        this.until = now + (this.big ? 120 + Math.random() * 260 : 60 + Math.random() * 140);
        this.text = this.big
          ? this.original
              .split('')
              .map(ch => (Math.random() > 0.55 ? String.fromCharCode(ch.charCodeAt(0) + Math.floor(Math.random() * 10) - 5) : ch))
              .join('')
          : this.original;
        const n = this.big ? 4 + ((Math.random() * 4) | 0) : 2 + ((Math.random() * 2) | 0);
        this.slices = Array.from({ length: n }, () => {
          const max = this.big ? 14 + Math.random() * 30 : 4 + Math.random() * 10;
          return { y: Math.random(), h: 0.04 + Math.random() * 0.18, dx: (Math.random() * 2 - 1) * max, max };
        });
        const d = this.big ? 3 + ((Math.random() * 5) | 0) : (Math.random() * 2) | 0;
        this.dropouts = Array.from({ length: d }, () => ({
          x: Math.random(),
          y: Math.random(),
          w: 0.05 + Math.random() * 0.2,
          h: 0.03 + Math.random() * 0.08
        }));
        this.rgb = this.big ? 6 + Math.random() * 10 : 2 + Math.random() * 3;
        this.jitterY = 0;
        return { text: this.text, active: true, slices: this.slices, dropouts: this.dropouts, rgb: this.rgb, jitterY: this.jitterY };
      }
    }
    return { text: this.original, active: false, slices: [], dropouts: [], rgb: 0, jitterY: 0 };
  }
}
